import React from 'react';
import Icon from '../../../components/AppIcon';

const Footer = () => {
  const currentYear = new Date()?.getFullYear();

  const footerSections = [
    {
      title: 'Product',
      links: [
        { label: 'API Documentation', href: '/api-documentation' },
        { label: 'Developer Portal', href: '/developer-portal' },
        { label: 'Pricing Plans', href: '/pricing-plans' },
        { label: 'Status Page', href: '#' },
        { label: 'Changelog', href: '#' },
        { label: 'Roadmap', href: '#' }
      ]
    },
    {
      title: 'Solutions',
      links: [
        { label: 'Customer Support', href: '#' },
        { label: 'Team Collaboration', href: '#' },
        { label: 'Gaming & Entertainment', href: '#' },
        { label: 'Healthcare', href: '#' },
        { label: 'Financial Services', href: '#' },
        { label: 'E-commerce', href: '#' }
      ]
    },
    {
      title: 'Developers',
      links: [
        { label: 'Getting Started', href: '/developer-portal' },
        { label: 'API Reference', href: '/api-documentation' },
        { label: 'SDKs & Libraries', href: '#' },
        { label: 'Code Examples', href: '#' },
        { label: 'Webhooks', href: '#' },
        { label: 'Community Forum', href: '#' }
      ]
    },
    {
      title: 'Company',
      links: [
        { label: 'About Us', href: '#' },
        { label: 'Contact Sales', href: '/contact-sales' },
        { label: 'Careers', href: '#' },
        { label: 'Blog', href: '#' },
        { label: 'Press Kit', href: '#' },
        { label: 'Partners', href: '#' }
      ]
    },
    {
      title: 'Support',
      links: [
        { label: 'Help Center', href: '#' },
        { label: 'Contact Support', href: '#' },
        { label: 'System Status', href: '#' },
        { label: 'Security', href: '#' },
        { label: 'Compliance', href: '#' },
        { label: 'Service Level Agreement', href: '#' }
      ]
    }
  ];

  const socialLinks = [
    { icon: 'Github', href: '#', label: 'GitHub' },
    { icon: 'Twitter', href: '#', label: 'Twitter' },
    { icon: 'Linkedin', href: '#', label: 'LinkedIn' },
    { icon: 'Youtube', href: '#', label: 'YouTube' },
    { icon: 'MessageCircle', href: '#', label: 'Discord' }
  ];

  const legalLinks = [
    { label: 'Privacy Policy', href: '#' },
    { label: 'Terms of Service', href: '#' },
    { label: 'Cookie Policy', href: '#' },
    { label: 'Data Processing Agreement', href: '#' },
    { label: 'Acceptable Use Policy', href: '#' }
  ];

  const trustBadges = [
    { name: 'SOC 2 Type II', icon: 'Shield' },
    { name: 'ISO 27001', icon: 'Lock' },
    { name: 'GDPR Compliant', icon: 'FileCheck' },
    { name: '99.9% Uptime', icon: 'Activity' }
  ];

  return (
    <footer className="bg-card border-t border-border">
      {/* Newsletter Section */}
      <div className="border-b border-border">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <div>
              <h3 className="text-2xl font-bold text-foreground mb-4">
                Stay updated with MessagingHub Pro
              </h3>
              <p className="text-muted-foreground">
                Get the latest updates on new features, API changes, and developer resources 
                delivered to your inbox.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <input
                type="email"
                placeholder="Enter your email address"
                className="flex-1 px-4 py-3 bg-input border border-border rounded-lg text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              />
              <button className="inline-flex items-center justify-center space-x-2 px-6 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors duration-200 whitespace-nowrap">
                <Icon name="Send" size={18} />
                <span>Subscribe</span>
              </button>
            </div>
          </div>
        </div>
      </div>
      {/* Main Footer Content */}
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-6 gap-8">
          {/* Brand Section */}
          <div className="lg:col-span-2">
            <div className="flex items-center space-x-3 mb-6">
              <div className="flex items-center justify-center w-10 h-10 bg-primary rounded-lg">
                <Icon name="MessageCircle" size={24} color="white" />
              </div>
              <span className="text-2xl font-bold text-foreground">
                MessagingHub Pro
              </span>
            </div>
            
            <p className="text-muted-foreground mb-6 leading-relaxed">
              Enterprise-grade messaging infrastructure that scales with your business. 
              Trusted by 10,000+ developers worldwide to power billions of messages.
            </p>

            {/* Trust Badges */}
            <div className="grid grid-cols-2 gap-3 mb-6">
              {trustBadges?.map((badge, index) => (
                <div key={index} className="flex items-center space-x-2 text-xs">
                  <Icon name={badge?.icon} size={14} className="text-accent" />
                  <span className="text-muted-foreground">{badge?.name}</span>
                </div>
              ))}
            </div>

            {/* Social Links */}
            <div className="flex space-x-4">
              {socialLinks?.map((social, index) => (
                <a
                  key={index}
                  href={social?.href}
                  className="w-10 h-10 bg-muted/50 rounded-lg flex items-center justify-center hover:bg-primary hover:text-primary-foreground transition-all duration-200"
                  aria-label={social?.label}
                >
                  <Icon name={social?.icon} size={18} />
                </a>
              ))}
            </div>
          </div>

          {/* Footer Links */}
          {footerSections?.map((section, index) => (
            <div key={index} className="lg:col-span-1">
              <h4 className="font-semibold text-foreground mb-4">
                {section?.title}
              </h4>
              <ul className="space-y-3">
                {section?.links?.map((link, linkIndex) => (
                  <li key={linkIndex}>
                    <a
                      href={link?.href}
                      className="text-sm text-muted-foreground hover:text-foreground transition-colors duration-200"
                    >
                      {link?.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
      {/* Bottom Bar */}
      <div className="border-t border-border">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-6">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-4">
            {/* Copyright */}
            <div className="text-sm text-muted-foreground">
              © {currentYear} MessagingHub Pro. All rights reserved.
            </div>

            {/* Legal Links */}
            <div className="flex flex-wrap items-center gap-6">
              {legalLinks?.map((link, index) => (
                <a
                  key={index}
                  href={link?.href}
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors duration-200"
                >
                  {link?.label}
                </a>
              ))}
            </div>

            {/* Status Indicator */}
            <div className="flex items-center space-x-2">
              <div className="w-2 h-2 bg-accent rounded-full animate-pulse"></div>
              <span className="text-sm text-muted-foreground">
                All systems operational
              </span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;